import { ChartDataOptionsBuilder } from './chart-data-options-builder';
import {
  createDefaultChartDataOptionsBuilder,
  DefaultChartDataOptionsBuilder
} from './default-chart-data-options-builder';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import { FavouriteDataOptions } from '../common/favourite-data-options';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { DrillDownHandler } from '../common/drill-down-builder';
import { AxisOptions } from 'highcharts';
import { ChartLayoutConfig } from '../common/layout-config';
import { getNumberOfRows } from '../common/chart-rows';
import { SeriesTypeColumnMappings } from 'app/components/favourite-layout/favourite/chart-data-options/types';

export class HeatmapChartDataOptionsBuilder implements ChartDataOptionsBuilder {

  private dataOptions: FavouriteDataOptions;
  private dataAdapter: FavouriteDataAdapter;
  private columnMappings: SeriesTypeColumnMappings = {
    mainSeries: {
      sel1: 'x',
      sel2: 'y',
      sel3: 'splitting',
      aggregation: 'value'
    }
  };

  public hasYAxis: boolean;

  private chartDataOptionsBuilder: DefaultChartDataOptionsBuilder;

  constructor(dataOptions: FavouriteDataOptions, dataAdapter: FavouriteDataAdapter, drillDownHandler: DrillDownHandler) {
    this.dataOptions = dataOptions;
    this.dataAdapter = dataAdapter;

    this.dataAdapter.hasTarget = () => false; // Not supported for heatmap
    this.dataAdapter.hasCategories = this.hasCategories;
    this.chartDataOptionsBuilder = createDefaultChartDataOptionsBuilder(dataOptions, dataAdapter, drillDownHandler, this.columnMappings);

    const yCol = this.colIndexOf('y', 'mainSeries');
    if (yCol >= 0) {
      this.hasYAxis = true;
      if (this.dataAdapter.hasCategories(yCol)) {
        // It looks better to reverse the ordering of y-axis categories in a heatmap if we have a "real" category axis
        this.dataAdapter.columnCategories[yCol].reverse();
      }
    }

    this.chartDataOptionsBuilder.pointDataOptionsBuilder.addStaticDataPointProperties = this.addStaticDataPointProperties;
    this.chartDataOptionsBuilder.pointDataOptionsBuilder.ignoreNullValuesForDataPoints = this.ignoreNullValuesForDataPoints;
    this.chartDataOptionsBuilder.seriesDataOptionsBuilder.shouldAddColorToDataPoint = this.shouldAddColorToDataPoint;
    this.chartDataOptionsBuilder.seriesDataOptionsBuilder.shouldAdjustDataPointLabelColor = this.shouldAdjustDataPointLabelColor;
    this.chartDataOptionsBuilder.isLegendEnabled = this.isLegendEnabled;
    this.chartDataOptionsBuilder.useLegendTitle = this.useLegendTitle;
    this.chartDataOptionsBuilder.createAxisHandler = this.createAxis;
    this.chartDataOptionsBuilder.createFakeAxisHandler = this.createFakeAxis;
    this.chartDataOptionsBuilder.legendTitleHandler = this.legendTitle;
    this.chartDataOptionsBuilder.getExtraGraphHeight = this.getExtraGraphHeight;
  }

  public setOptions(options: Highcharts.Options, splitRow: FavouriteRowValue, seriesValues: FavouriteRowValue[]) {
    this.chartDataOptionsBuilder.setOptions(options, splitRow, seriesValues);
    this.defineAggregationValueFormatter(options);
  }

  public colIndexOf(typeName: string, seriesType: string) {
    return this.chartDataOptionsBuilder.colIndexOf(typeName, seriesType);
  }

  public isXAxisReversedByDefault() {
    return this.chartDataOptionsBuilder.isXAxisReversedByDefault();
  }

  private defineAggregationValueFormatter(options: any): void {

    const dataAdapter = this.dataAdapter;

    options.series[0].dataLabels = options.series[0].dataLabels || {};
    options.series[0].dataLabels.formatter = function () {
      return dataAdapter.format(dataAdapter.aggrColIndex, this.point.value);
    };
  }

  private createFakeAxis = (options: Highcharts.Options, axisType: string) => {
    const axisObj: AxisOptions = {
      title: {
        text: ''
      },
      type: 'category',
      categories: ['']
    };
    options[axisType + 'Axis'] = axisObj;
  };

  private createAxis = (colIndex: number, axisObj: Highcharts.AxisOptions, axisName: string) => {
    if (this.dataAdapter.hasCategories(colIndex)) {
      // Workaround provided by Highcharts for a bug
      // https://github.com/highslide-software/highcharts.com/issues/4385#issuecomment-120926087
      // http://jsfiddle.net/61343dsm/4/
      axisObj.tickPositioner = function () {
        const positions: number[] = [];
        // eslint:disable-next-line
        let tick = Math.floor(this.min);
        const increment = 1;

        // eslint:disable-next-line
        for (tick; tick - increment < this.max; tick += increment) {
          positions.push(tick);
        }
        return positions;
      };
    }
  };

  private useDataPointColors = () => {
    const config = this.dataAdapter.layoutConfig as ChartLayoutConfig;
    const useDataPointColors = config.chartStyle('heatmapUseDataPointColors');

    return useDataPointColors === true;
  };

  private shouldAdjustDataPointLabelColor = () => {
    return this.useDataPointColors();
  };

  private isLegendEnabled = () => {
    return !this.useDataPointColors();
  };

  private useLegendTitle = () => {
    return !this.useDataPointColors();
  };

  private legendTitle = (seriesCol: number): string => {
    return this.dataOptions.getColumnTitle(this.colIndexOf('value', 'mainSeries'), 'legend');
  };

  private shouldAddColorToDataPoint = (seriesIndex: number) => {
    return this.useDataPointColors();
  };

  private addStaticDataPointProperties = (dataPoint: Highcharts.PointOptionsObject) => {
    if (!this.hasYAxis) {
      dataPoint.y = 0;
    }
  };

  private ignoreNullValuesForDataPoints = (axisColIndex: number): boolean => {
    // Use super.hasCategories to check for datatype the ordinary way
    return !this.dataAdapter.hasCategories(axisColIndex);
  };

  private hasCategories = (axisColIndex: number): boolean => {
    return axisColIndex >= 0;
  };

  private getExtraGraphHeight(options: Highcharts.Options, dataAdapter: FavouriteDataAdapter) {

    const config = dataAdapter.layoutConfig as ChartLayoutConfig;

    const rowHeight = config.chartStyle('heatmapHeightGrowthYAxisCategory');
    const allowedNumberOfRows = config.chartStyle('heatmapHeightGrowIfMoreThan') || 0;
    const yColumnIndex = this.colIndexOf('y', 'mainSeries');

    return rowHeight
      ? Math.max(rowHeight * (getNumberOfRows(options, dataAdapter, yColumnIndex) - allowedNumberOfRows), 0)
      : 0;
  }
}
